import { z } from 'zod'
import config from '../config'
import camelCase from '../utilities/camelCase'

export type FieldErrors = { [name: string]: string[] }

const apiErrorParser = z.object({
  message: z.string(),
  errors: z.record(z.array(z.string())).nullish(),
})

export class ApiError extends Error {
  code: number
  fields?: FieldErrors

  constructor(message: string, code: number, fields?: FieldErrors) {
    super(message)
    this.code = code
    this.fields = fields
  }
}

async function request(method: string, path: string, body?: any) {
  const req: RequestInit = {
    method,
    headers: {
      accept: 'application/json',
    },
  }
  if (body) {
    ;(req.headers as any)['content-type'] = 'application/json'
    req.body = JSON.stringify(body)
  }

  const res = await fetch(new URL(path, config.apiUrl).toString(), req)

  if (res.status >= 300) {
    let err
    try {
      const { message, errors } = apiErrorParser.parse(await res.json())
      err = new ApiError(message, res.status, errors || undefined)
    } catch (_) {
      err = new ApiError('Parsing error response failed', res.status, {})
    }
    throw err
  }

  const txt = await res.text()
  if (!txt) {
    return null
  }
  return camelCase(JSON.parse(txt))
}

async function requestForm(
  method: string,
  path: string,
  body: Record<string, string | File>,
  onProgress?: (e: ProgressEvent) => void
) {
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest()

    req.open(method, new URL(path, config.apiUrl).toString())

    req.setRequestHeader('accept', 'application/json')
    req.withCredentials = true

    req.addEventListener('readystatechange', () => {
      if (req.readyState === XMLHttpRequest.DONE) {
        if (req.status >= 300) {
          let err
          try {
            const { message, errors } = apiErrorParser.parse(JSON.parse(req.responseText))
            err = new ApiError(message, req.status, errors || undefined)
          } catch (_) {
            err = new ApiError('Parsing error response failed', req.status, {})
          }
          reject(err)
        }

        const txt = req.responseText
        if (!txt) {
          return null
        }
        resolve(camelCase(JSON.parse(txt)))
      }
    })

    if (onProgress) {
      req.upload.addEventListener('progress', onProgress)
    }

    if (body) {
      const formdata = new FormData()
      for (const [key, value] of Object.entries(body)) {
        if (value !== undefined) {
          formdata.append(key, value)
        }
      }
      req.send(formdata)
    } else {
      req.send()
    }
  })
  //
  //
  //
  //
  // const req: RequestInit = {
  //   method,
  //   headers: {
  //     accept: 'application/json',
  //   },
  // }
  //
  // //  ;(req.headers as any)['content-type'] = 'multipart/form-data'
  // //req.body = JSON.stringify(body)
  // req.body = formdata
  //
  // const res = await fetch(new URL(path, config.apiUrl).toString(), req)
  //
  // if (res.status >= 300) {
  //   let err
  //   try {
  //     const { message, errors } = apiErrorParser.parse(await res.json())
  //     err = new ApiError(message, res.status, errors || undefined)
  //   } catch (_) {
  //     err = new ApiError('Parsing error response failed', res.status, {})
  //   }
  //   throw err
  // }
  //
  // const txt = await res.text()
  // if (!txt) {
  //   return null
  // }
  // return camelCase(JSON.parse(txt))
}

export const get = (path: string) => request('GET', path)
export const post = (path: string, body: any) => request('POST', path, body)
export const postForm = (path: string, body: any, onProgress?: (e: ProgressEvent) => void) =>
  requestForm('POST', path, body, onProgress)
export const put = (path: string, body: any) => request('PUT', path, body)
export const patch = (path: string, body: any) => request('PATCH', path, body)
export const del = (path: string, body: any) => request('DELETE', path, body)
export const url = (path: string) => new URL(path, config.apiUrl).toString()
